import { SxProps, Theme } from '@mui/material';
import React, { memo, useMemo } from 'react';

import { Autocomplete, AutocompleteOption } from '~/components/Autocomplete';
import { Checkbox } from '~/components/Checkbox';
import { DateField } from '~/components/DateField';
import { TextField } from '~/components/TextField';
import { GetCompanyResponse } from '~/services/rtkQuery/company/types';

import { Investors } from './components/Investors';
import styles from './styles.module.scss';

const EMPTY_OPTIONS: AutocompleteOption[] = [];

const LONG_TEXT_FIELD_SPAN_2_SX: SxProps<Theme> = {
  gridColumn: 'span 2',
  '& .MuiInputBase-input': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
  },
};

const LONG_TEXT_FIELD_SPAN_3_SX: SxProps<Theme> = {
  gridColumn: 'span 3',
  '& .MuiInputBase-input': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
  },
};

const LONG_TEXT_FIELD_SPAN_4_SX: SxProps<Theme> = {
  gridColumn: 'span 4',
  '& .MuiInputBase-input': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
  },
};

interface Props {
  data: GetCompanyResponse['result']['data']['rounds'][number]['sub_rounds'][number];
  roundIndex: number;
}

const Component: React.FC<Props> = ({ data, roundIndex }) => {
  const dealDate = useMemo(() => {
    return new Date(data.deal_date);
  }, [data.deal_date]);

  const announcedDate = useMemo(() => {
    return data.announced_date ? new Date(data.announced_date) : null;
  }, [data.announced_date]);

  const selectedCeoEducation = useMemo<AutocompleteOption | undefined>(() => {
    return data.ceo_education
      ? { value: data.ceo_education.id, label: data.ceo_education.name }
      : undefined;
  }, [data.ceo_education]);

  const selectedSecurityType = useMemo<AutocompleteOption | undefined>(() => {
    return data.security_type
      ? { value: data.security_type.id, label: data.security_type.name }
      : undefined;
  }, [data.security_type]);

  return (
    <div className={styles.subround}>
      <div className={styles.subround__fields}>
        <DateField disabled label="Deal Date*" value={dealDate} />
        <DateField
          disabled
          emptyDatePlaceholder="No announcement"
          label="Announced Date*"
          value={announcedDate}
        />
        <TextField disabled label="Deal Size*" value={data.deal_size} variant="outlined" />
        {roundIndex === 2 && (
          <Autocomplete
            disabled
            options={EMPTY_OPTIONS}
            placeholder="Security Type*"
            value={selectedSecurityType}
          />
        )}
        {roundIndex === 2 &&
          (selectedSecurityType?.value === 2 || selectedSecurityType?.value === 3) && (
            <>
              <TextField disabled label="CAP*" value={data.cap} variant="outlined" />
              <TextField disabled label="%Discount*" value={data.discount} variant="outlined" />
            </>
          )}
        {roundIndex === 2 && selectedSecurityType?.value === 3 && (
          <>
            <TextField disabled label="%Interest*" value={data.interest} variant="outlined" />
            <TextField
              disabled
              label="Conversion Amount*"
              value={data.conversion_amount}
              variant="outlined"
            />
          </>
        )}
        <TextField
          disabled
          label={`${
            roundIndex === 2 &&
            (selectedSecurityType?.value === 2 || selectedSecurityType?.value === 3)
              ? 'Effective '
              : ''
          }Pre-Money Valuation*`}
          value={data.pre_money_valuation || data.effective_pre_money_valuation}
          variant="outlined"
        />
        <TextField
          disabled
          label={`${
            roundIndex === 2 &&
            (selectedSecurityType?.value === 2 || selectedSecurityType?.value === 3)
              ? 'Effective '
              : ''
          }Post Money Valuation*`}
          value={data.post_money_valuation || data.effective_post_money_valuation}
          variant="outlined"
        />
        <TextField disabled label="%Acquired*" value={data.acquired} variant="outlined" />
        <TextField disabled label="Acquired (calc)" value={data.acquired_calc} variant="outlined" />
        <TextField disabled label="Employees*" value={data.employees} variant="outlined" />
        {(roundIndex === 0 || data.ceo_changed) && (
          <>
            <TextField disabled label="CEO Name*" value={data.ceo_name} variant="outlined" />
            <Autocomplete
              disabled
              options={EMPTY_OPTIONS}
              placeholder="CEO Education*"
              value={selectedCeoEducation}
            />
            <TextField
              disabled
              label="CEO Education Details"
              sx={
                roundIndex === 2
                  ? selectedSecurityType?.value === 2
                    ? LONG_TEXT_FIELD_SPAN_3_SX
                    : LONG_TEXT_FIELD_SPAN_4_SX
                  : LONG_TEXT_FIELD_SPAN_2_SX
              }
              title={data.ceo_education_history || ''}
              value={data.ceo_education_history}
              variant="outlined"
            />
            <TextField
              disabled
              label="CEO Biography"
              sx={LONG_TEXT_FIELD_SPAN_4_SX}
              title={data.ceo_biography || ''}
              value={data.ceo_biography}
              variant="outlined"
            />
          </>
        )}
      </div>
      <div className={styles.subround__checkboxes}>
        <Checkbox
          disabled
          checked={roundIndex === 0 ? data.ceo_is_founder : data.ceo_changed}
          containerClassName={styles.subround__checkboxes__checkbox}
          label={roundIndex === 0 ? 'CEO is a Founder' : 'CEO Changed'}
        />
        <Checkbox
          disabled
          checked={data.generating_revenue}
          containerClassName={styles.subround__checkboxes__checkbox}
          label="Generating Revenue"
        />
        <Checkbox
          disabled
          checked={data.participating_preferred}
          containerClassName={styles.subround__checkboxes__checkbox}
          label="Participating Preferred"
        />
        {roundIndex !== 0 && (
          <>
            <Checkbox
              disabled
              checked={data.revenue_status_changed}
              containerClassName={styles.subround__checkboxes__checkbox}
              label="Revenue Status changed"
            />
            <Checkbox
              disabled
              checked={data.generating_profit}
              containerClassName={styles.subround__checkboxes__checkbox}
              label="Generating Profit"
            />
          </>
        )}
        <Checkbox
          disabled
          checked={!data.is_announced}
          containerClassName={styles.subround__checkboxes__checkbox}
          label="No announcement"
        />
      </div>
      <Investors data={data.investors} />
    </div>
  );
};

export const Subround = memo(Component);
